import { Badge, GridCellText, Icon, type TableColumn } from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { getCivilSearchers, type ProjectUtilityCheck } from '~/api/civilsearch';

export function useColumns(): TableColumn<ProjectUtilityCheck>[] {
  const { data: civilSearchers = [] } = useQuery({
    queryKey: ['civilSearchers'],
    queryFn: getCivilSearchers,
  });

  const assigneeOptions = useMemo(
    () => civilSearchers.map((s) => ({ value: s.userId, label: s.name || s.email })),
    [civilSearchers],
  );

  return [
    {
      id: 'completedUtilityChecks',
      header: 'SSWEG Completion',
      accessorKey: 'completedUtilityChecks',
      size: 140,
      cell: ({ getValue }) => (
        <GridCellText>
          <Badge color={getValue<number>() >= 5 ? 'green' : 'yellow'}>
            {getValue<number>() >= 5 ? 'Complete' : `${getValue<number>()}/5`}
          </Badge>
        </GridCellText>
      ),
      meta: {
        filterLabel: 'SSWEG Completion',
        filterOperatorTypeConfig: [
          { id: 'eq', label: 'Is' },
          { id: 'neq', label: 'Is Not' },
        ],
        filterValueConfig: {
          type: 'select',
          options: [{ value: '5', label: 'Complete' }],
        },
      },
    },
    {
      id: 'organizationName',
      header: 'Organization',
      accessorKey: 'organizationName',
      size: 200,
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Organization',
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
    },
    {
      id: 'projectName',
      header: 'Project',
      accessorKey: 'projectName',
      size: 250,
      cell: (cell) => {
        const id = cell.row.original.projectId;
        const value = cell.getValue<string>();
        const link = `${window.location.origin}/projects/${id}/utilities`;

        return (
          <GridCellText>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-dark underline flex flex-row items-center"
            >
              <span>{value}</span>
            </a>
          </GridCellText>
        );
      },
      meta: {
        filterLabel: 'Project',
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
    },
    {
      id: 'projectShortId',
      header: 'Project ID',
      accessorKey: 'projectShortId',
      size: 140,
      cell: (cell) => {
        const id = cell.row.original.projectId;
        const value = cell.getValue<string>();
        const link = `${window.location.origin}/projects/${id}/utilities`;

        return (
          <GridCellText>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-dark hover:underline flex flex-row items-center"
            >
              <span>{value}</span>
              <Icon icon="externalLink" />
            </a>
          </GridCellText>
        );
      },
      meta: {
        filterLabel: 'Project ID',
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
    },
    {
      id: 'majorityAssigneeId',
      header: 'Assignee',
      accessorKey: 'majorityAssigneeName',
      size: 180,
      cell: ({ row }) => {
        const { majorityAssigneeName } = row.original;
        if (!majorityAssigneeName) return <GridCellText>—</GridCellText>;
        return <GridCellText>{majorityAssigneeName}</GridCellText>;
      },
      meta: {
        filterLabel: 'Assignee',
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: assigneeOptions,
        },
      },
    },
    {
      id: 'submittedDate',
      header: 'Submitted',
      accessorFn: (row) =>
        row.submittedDate ? DateTime.fromSQL(row.submittedDate).toLocaleString(DateTime.DATE_MED) : '',
      size: 130,
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Submitted',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'completionPercentage',
      header: 'CS Big 5 %',
      accessorKey: 'completionPercentage',
      size: 130,
      cell: ({ getValue }) => (
        <GridCellText>
          <span className="font-medium">{getValue<number>()}%</span>
        </GridCellText>
      ),
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
      },
    },
  ];
}
